<template>
  <div class="tw:flex tw:md:hidden">
    <MobileInformationCard title="rc_error_message_mobile_header" subtitle="rc_error_message_mobile_text" />
  </div>
  <div class="tw:md:flex tw:hidden tw:flex-col">
    <div v-if="requiredDataHasErrors">
      <DataErrorView />
    </div>
    <div v-if="!requiredDataIsBeingFetched && !requiredDataHasErrors">
      <PerformanceArea
        :key="formattedStartDate"
        :hasNoBookings="hasNoBookings"
        :hasEmptyStatsTable="hasEmptyStatsTable"
        :hasEmptyBookingTable="hasBookings && bookingLogData.length === 0"
        :hasNoData="hasNoData"
        :overviewTableCampaignType="overviewTableCampaignType"
        :bookingTableCampaignType="bookingTableCampaignType"
        :selectedArea="selectedPerformanceArea"
        :currentCampaignType="currentCampaignType"
        :prettyKpis="prettyKpis"
        :lastUpdated="lastUpdatedLocalized"
        :markets="markets"
        :bookings="bookings"
        :cpa="cpa"
        :visits="visits"
        :revenue="revenue"
        :categories="categories"
        :campaignTypes="campaignTypes"
        :tickPositions="tickPositions"
      />
    </div>
  </div>
  <FullScreenLoader v-show="requiredDataIsBeingFetched" />
</template>

<script setup>
import { computed, onMounted } from 'vue'

import PerformanceArea from '@/components/rateConnect/performance/PerformanceArea.vue'
import DataErrorView from '@/components/rateConnect/performance/DataErrorView.vue'
import MobileInformationCard from '@/components/rateConnect/performance/MobileInformationCard.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'

import { useBookingLog, useKpis, useStats } from '@/components/rateConnect/queries'
import { useRateConnectPerformance, usePrettyStats } from './useRateConnectPerformance'
import { selectedAccommodationId } from '@/layouts/queries'
import { campaignsAggregateType } from '@/utils/rateConnectUtils'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const props = defineProps({
  selectedPerformanceArea: String,
  markets: {
    type: Object,
    default: null,
  },
  currentCampaignType: {
    type: String,
    default: '',
  },
})

const {
  selectedMarket,
  formattedStartDate,
  formattedEndDate,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedAggregation,
  selectedDateType,
  selectedStatus,
  resetData,
} = useRateConnectPerformance()

const { language, currency } = useSessionValues()
const { formatDate } = useFormatting()
const {
  isFetching: isKpisFetching,
  isError: isKpisError,
  KpisData,
  prettyKpis,
} = useKpis(currency, formattedStartDate, formattedEndDate, selectedMarket)
const {
  isFetching: isBookingLogFetching,
  isError: isBookingLogError,
  bookingLogData,
  hasBookings,
} = useBookingLog(
  selectedAccommodationId,
  selectedDateType,
  currency,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedMarket,
  selectedStatus
)
const {
  isFetching: isStatsFetching,
  isError: isStatsError,
  statsData,
  hasStatistics,
} = useStats(currency, formattedStartDate, formattedEndDate, selectedMarket, selectedAggregation)
const lastUpdated = computed(() => KpisData.value?.lastUpdated)
const lastUpdatedLocalized = computed(() => {
  return KpisData.value?.lastUpdated
    ? formatDate(KpisData.value.lastUpdated, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
    : null
})

const overviewTableCampaignType = computed(() => {
  if (!statsData.value || !props.currentCampaignType) return null
  return campaignsAggregateType(statsData.value, props.currentCampaignType)
})

const bookingTableCampaignType = computed(() => {
  if (bookingLogData.value.length === 0) return props.currentCampaignType === 'cpc' ? 'cpc' : 'cpa'
  if (bookingLogData.value?.every(item => item?.campaignType?.toLowerCase() === 'netcpa')) return 'cpa'
  if (bookingLogData.value?.every(item => item.campaignType === 'cpc')) return 'cpc'
  else return 'both'
})

const { bookings, visits, revenue, cpa, categories, campaignTypes, tickPositions } = usePrettyStats(
  statsData,
  lastUpdated,
  language
)

const requiredDataHasErrors = computed(() => isKpisError.value || isBookingLogError.value || isStatsError.value)
const requiredDataIsBeingFetched = computed(
  () => isKpisFetching.value || isBookingLogFetching.value || isStatsFetching.value
)

const hasEmptyStatsTable = computed(() => {
  return statsData.value?.length === 0 && hasStatistics.value
})

const hasNoData = computed(() => {
  if (requiredDataIsBeingFetched.value) {
    return false
  }

  return (
    statsData.value?.length === 0 && !hasStatistics.value && !hasBookings.value && bookingLogData.value?.length === 0
  )
})

const hasNoBookings = computed(() => {
  if (requiredDataIsBeingFetched.value) {
    return false
  }

  return (
    (statsData.value?.length > 0 || hasStatistics.value) && !hasBookings.value && bookingLogData.value?.length === 0
  )
})

onMounted(() => {
  resetData()
})
</script>
